import { Suspense, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom-v5-compat';
import { Toaster } from 'sonner';

import { ImpersonationBanner } from '../features/impersonation/ImpersonationBanner';
import { useIsAdmin } from '../hooks/useCurrentUser';
import { useMobileApp } from '../hooks/useMobileApp';
import { cn } from '../lib/cn';
import { Header } from './Header';
import { MOBILE_HOME, isMobileAllowedPath } from './nav';
import { Sidebar } from './Sidebar';
import { SidebarCollapseProvider, useSidebarCollapse } from './sidebarCollapse';
import { ThemeProvider, themeMode, themeRootClass, useTheme } from './theme';

import './ghost.css';
import './new-vegas.css';

// New Vegas fisheye: a displacement map that bows the CRT overlay like curved
// glass. Two gradients — red varies left→right, green top→bottom — on an
// ease-in-out S-curve (near-flat at center, steep toward the edges) so the
// bulge concentrates at the rim the way a real lens does. Built as inline SVG
// data URIs and fed to #nv-warp's feDisplacementMap (R→x, G→y).
const dispMap = (axis: 'x' | 'y') => {
    const chan = (v: number) => (axis === 'x' ? `rgb(${v},0,0)` : `rgb(0,${v},0)`);
    const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'>` +
        `<linearGradient id='g' x1='0' y1='0' x2='${axis === 'x' ? 1 : 0}' y2='${axis === 'x' ? 0 : 1}'>` +
        `<stop offset='0' stop-color='${chan(0)}'/>` +
        `<stop offset='.25' stop-color='${chan(90)}'/>` +
        `<stop offset='.5' stop-color='${chan(128)}'/>` +
        `<stop offset='.75' stop-color='${chan(166)}'/>` +
        `<stop offset='1' stop-color='${chan(255)}'/>` +
        `</linearGradient><rect width='100' height='100' fill='url(#g)'/></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};
const NV_WARP_X = dispMap('x');
const NV_WARP_Y = dispMap('y');

// In the installed mobile PWA, only routes flagged `mobile` in nav.ts are
// reachable. Anything else (deep links, manual nav, the /v2 index redirect)
// bounces to MOBILE_HOME. On desktop this is a no-op pass-through.
function MobileGuard({ children }: { children: ReactNode }) {
    const isMobileApp = useMobileApp();
    const isAdmin = useIsAdmin();
    const { pathname } = useLocation();
    // Admins roam freely on mobile; everyone else is pinned to the mobile-enabled
    // routes (Tickete + Rapoarte zilnice).
    if (isMobileApp && !isAdmin && !isMobileAllowedPath(pathname)) {
        return <Navigate to={MOBILE_HOME} replace />;
    }
    return <>{children}</>;
}

function ShellInner() {
    const { theme } = useTheme();
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const { collapsed, toggle: toggleCollapsed } = useSidebarCollapse();

    return (
        <div className={cn(themeRootClass(theme))}>
            {/* New Vegas CRT screen-curvature filter — referenced by the .v2-root::after
                screen overlay (scanlines + vignette) only, so the glass layer bows without
                distorting the actual UI elements. Smooth radial barrel: two S-curve gradient
                maps (R = horizontal, G = vertical) drive the displacement so the overlay
                bulges like curved glass, replacing the old fractal-noise wobble. Flip `scale`
                negative for pincushion; raise/lower it to taste. */}
            {theme === 'newvegas' && (
                <svg aria-hidden="true" width="0" height="0" style={{ position: 'absolute' }}>
                    <filter id="nv-warp" x="-6%" y="-6%" width="112%" height="112%">
                        <feImage href={NV_WARP_X} xlinkHref={NV_WARP_X} preserveAspectRatio="none" result="mx" />
                        <feImage href={NV_WARP_Y} xlinkHref={NV_WARP_Y} preserveAspectRatio="none" result="my" />
                        <feBlend in="mx" in2="my" mode="screen" result="map" />
                        <feDisplacementMap
                            in="SourceGraphic"
                            in2="map"
                            scale="26"
                            xChannelSelector="R"
                            yChannelSelector="G"
                        />
                    </filter>
                </svg>
            )}
            <div className="v2-root flex h-screen overflow-hidden bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                <Toaster
                    richColors
                    closeButton
                    position="top-right"
                    theme={themeMode(theme)}
                />
                <Sidebar
                    isOpen={isSidebarOpen}
                    collapsed={collapsed}
                    onNavigate={() => setSidebarOpen(false)}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                    <Header
                        onToggleSidebar={() => setSidebarOpen((open) => !open)}
                        collapsed={collapsed}
                        onToggleCollapse={toggleCollapsed}
                    />
                    <ImpersonationBanner />
                    <main className="flex-1 overflow-y-auto">
                        <Suspense
                            fallback={
                                <div className="flex h-64 w-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                                    Se incarca...
                                </div>
                            }
                        >
                            <MobileGuard>
                                <Outlet />
                            </MobileGuard>
                        </Suspense>
                    </main>
                </div>
            </div>
        </div>
    );
}

export function Shell() {
    return (
        <ThemeProvider>
            <SidebarCollapseProvider>
                <ShellInner />
            </SidebarCollapseProvider>
        </ThemeProvider>
    );
}
